import React, {useCallback} from 'react';
import Wysiwyg from 'components/Wysiwyg';
import {
  ElementProps,
  TextBlockElementClass,
} from 'types/documentTemplateElement';
import {useSelector} from 'react-redux';
import {RootState} from '../../../store/store';

interface TextBlockElementProps extends ElementProps<TextBlockElementClass> {}

const replaceTemplateTags = (
  html: string,
  tagValues: Record<string, string>,
): string => {
  return html.replace(
    /<span([^>]*data-tag-value="(\{([^}]+)\})"[^>]*)>(.*?)<\/span>/g,
    (match, spanAttributes, fullTag, tagName, innerContent) => {
      const replacement = tagValues[tagName] || innerContent;
      return `<span${spanAttributes}>${replacement}</span>`;
    },
  );
};

const TextBlockElementComponent: React.FC<TextBlockElementProps> = ({
  element,
  updateElement,
  mode,
}) => {
  const userApplication = useSelector(
    (state: RootState) => state.userApplication.userApplication,
  );
  const company = useSelector((state: RootState) => state.company.company);

  const handleContentChange = useCallback(
    (content: string) => {
      const updatedElement = new TextBlockElementClass({
        ...element,
        properties: {
          ...element.properties,
          content,
        },
      });
      updateElement(updatedElement);
    },
    [element, updateElement],
  );

  if (mode === 'preview') {
    return (
      <div
        className="prose max-w-none w-full p-3 preview-content"
        dangerouslySetInnerHTML={{
          __html: element.properties.content || '',
        }}
      />
    );
  }

  if (mode === 'admin') {
    return (
      <div
        className="prose max-w-none w-full px-3 pb-2 preview-content"
        dangerouslySetInnerHTML={{__html: element.properties.value || ''}}
      />
    );
  }

  if (mode === 'view') {
    const tagValues = {
      EMPLOYEE_FIRST_NAME: userApplication?.user?.firstName || '',
      EMPLOYEE_LAST_NAME: userApplication?.user?.lastName || '',
      EMPLOYEE_FULL_NAME: `${userApplication?.user?.firstName || ''} ${userApplication?.user?.lastName || ''}`,
      EMPLOYEE_SUPERVISOR:
        userApplication?.user?.supervisors
          ?.map(
            (supervisor: {firstName: string; lastName: string}) =>
              `${supervisor.firstName} ${supervisor.lastName}`,
          )
          .join(', ') || '',
      EMPLOYEE_ROLE:
        userApplication?.user?.jobRoles
          ?.map((role: {name: string}) => role.name)
          .join(', ') || '',
      EMPLOYEE_LOCATION:
        userApplication?.user?.locations
          ?.map((location: {name: string}) => location.name)
          .join(', ') || '',
      CURRENT_DATE: new Date().toLocaleDateString(),
      COMPANY_NAME: company?.name,
    };

    const processedContent = replaceTemplateTags(
      element.properties.content || '',
      tagValues,
    );

    if (processedContent !== element.properties.value) {
      updateElement(
        new TextBlockElementClass({
          ...element,
          properties: {...element.properties, value: processedContent},
        }),
      );
    }

    return (
      <div
        className="prose max-w-none w-full px-3 pb-2 preview-content"
        dangerouslySetInnerHTML={{__html: processedContent}}
      />
    );
  }

  return (
    <div className="w-full m-8">
      <Wysiwyg
        content={element.properties.content}
        onContentChange={handleContentChange}
        key={element.id}
      />
    </div>
  );
};

export default TextBlockElementComponent;
